<template>
  <div class="session-metrics-chart">
    <!-- Chart header with controls and max power -->
    <div class="chart-header">
      <!-- Additional metrics toggle buttons -->
      <div class="chart-controls" v-if="availableMetrics.length > 0">
        <span class="controls-label">{{ $t('session-chart.additional-metrics') }}:</span>
        <v-chip
          v-for="metric in availableMetrics"
          :key="metric.key"
          :color="isMetricSelected(metric.key) ? metric.color : 'default'"
          :outlined="!isMetricSelected(metric.key)"
          :text-color="isMetricSelected(metric.key) ? 'white' : undefined"
          small
          @click="toggleMetric(metric.key)"
        >
          <v-icon small left>{{ metric.icon }}</v-icon>
          {{ metric.label }}
        </v-chip>
      </div>
      <div class="chart-controls" v-else></div>

      <!-- Max power badge -->
      <div class="max-power-badge" v-if="maxPower > 0">
        <v-icon small color="#E34A33">mdi-lightning-bolt</v-icon>
        <span class="max-power-label">Max:</span>
        <span class="max-power-value">{{ maxPower.toFixed(1) }} kW</span>
      </div>
    </div>

    <!-- Highcharts component -->
    <highcharts
      v-if="hasData"
      :options="chartOptions"
      ref="chart"
    />

    <!-- No data placeholder -->
    <div v-else class="no-data-placeholder">
      <v-icon large color="grey lighten-1">mdi-chart-line-variant</v-icon>
      <p>{{ $t('session-chart.no-data') }}</p>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import dayjs from 'dayjs'

export default {
  name: 'SessionMetricsChart',

  components: {
    highcharts: Chart,
  },

  props: {
    metrics: {
      type: Array,
      default: () => [],
    },
    events: {
      type: Array,
      default: () => [],
    },
    startTime: {
      type: String,
      default: null,
    },
    stopTime: {
      type: String,
      default: null,
    },
    height: {
      type: Number,
      default: 300,
    },
  },

  data() {
    return {
      selectedMetrics: [], // Array of selected additional metrics
    }
  },

  computed: {
    hasData() {
      return this.metrics && this.metrics.length > 0
    },

    hasEvents() {
      return this.events && this.events.length > 0
    },

    // Check if power data is directly provided
    hasPowerData() {
      return this.metrics.some(m =>
        m.measures?.some(x => x.measurand === 'Power.Active.Import')
      )
    },

    // Get the start meter value (first energy reading) to use as baseline
    startMeterKwh() {
      if (!this.hasData) return 0
      const firstMetric = this.metrics[0]
      const energyMeasure = firstMetric?.measures?.find(x => x.measurand === 'Energy.Active.Import.Register')
      if (energyMeasure) {
        return this.normalizeToKwh(energyMeasure.value, energyMeasure.unit)
      }
      return 0
    },

    // Process metrics to extract all data
    processedData() {
      if (!this.hasData) return []

      const startMeter = this.startMeterKwh
      const data = []
      for (let i = 0; i < this.metrics.length; i++) {
        const m = this.metrics[i]
        const timestamp = new Date(m.date).getTime()
        const energyMeasure = m.measures?.find(x => x.measurand === 'Energy.Active.Import.Register')
        const powerMeasure = m.measures?.find(x => x.measurand === 'Power.Active.Import')

        // Get absolute energy in kWh (for power calculation)
        let absoluteEnergy = 0
        // Get relative energy (starts at 0 from startMeter)
        let relativeEnergy = 0
        if (energyMeasure) {
          absoluteEnergy = this.normalizeToKwh(energyMeasure.value, energyMeasure.unit)
          relativeEnergy = absoluteEnergy - startMeter
        }

        // Get power in kW (use provided or calculate from energy delta)
        let power = 0
        let powerCalculated = false
        if (powerMeasure) {
          power = this.normalizeToKw(powerMeasure.value, powerMeasure.unit)
        } else if (energyMeasure && i > 0) {
          const prevMetric = this.metrics[i - 1]
          const prevEnergy = prevMetric.measures?.find(x => x.measurand === 'Energy.Active.Import.Register')
          if (prevEnergy) {
            const prevE = this.normalizeToKwh(prevEnergy.value, prevEnergy.unit)
            const deltaE = absoluteEnergy - prevE
            const deltaT = (new Date(m.date) - new Date(prevMetric.date)) / 3600000
            power = deltaT > 0 ? Math.max(deltaE / deltaT, 0) : 0
            powerCalculated = true
          }
        }

        // Collect other measurements
        const otherMeasures = {}
        for (const measure of (m.measures || [])) {
          if (measure.measurand !== 'Energy.Active.Import.Register' && measure.measurand !== 'Power.Active.Import') {
            const key = measure.phase ? `${measure.measurand}_${measure.phase}` : measure.measurand
            otherMeasures[key] = {
              value: measure.value,
              unit: measure.unit,
              phase: measure.phase,
            }
          }
        }

        data.push({
          timestamp,
          energy: relativeEnergy, // Use relative energy (starts at 0)
          absoluteEnergy, // Keep absolute for reference if needed
          power,
          powerCalculated,
          ...otherMeasures,
        })
      }
      return data
    },

    // Power series data
    powerSeriesData() {
      return this.processedData.map(d => [d.timestamp, Math.round(d.power * 100) / 100])
    },

    // Max power value
    maxPower() {
      if (!this.processedData.length) return 0
      return Math.max(...this.processedData.map(d => d.power))
    },

    // Energy series data
    energySeriesData() {
      return this.processedData.map(d => [d.timestamp, Math.round(d.energy * 100) / 100])
    },

    // Event plot lines
    eventPlotLines() {
      if (!this.hasEvents) return []

      const eventConfig = {
        START: { color: '#10B981', label: 'Démarrage' },
        STOP: { color: '#EF4444', label: 'Arrêt' },
        SUSPENDED: { color: '#F59E0B', label: 'Suspendu' },
        RESUMED: { color: '#3B82F6', label: 'Reprise' },
        ERROR: { color: '#DC2626', label: 'Erreur' },
      }

      return this.events.map(e => {
        const config = eventConfig[e.type] || { color: '#6B7280', label: e.type }
        return {
          color: config.color,
          width: 2,
          value: new Date(e.date).getTime(),
          dashStyle: 'Dash',
          zIndex: 5,
          label: {
            text: config.label,
            rotation: 0,
            style: {
              color: config.color,
              fontSize: '10px',
              fontWeight: 'bold',
            },
          },
        }
      })
    },

    // Available additional metrics
    availableMetrics() {
      if (!this.hasData) return []
      const available = []

      const measurandChecks = [
        { key: 'SoC', label: 'SoC (%)', icon: 'mdi-battery-charging', color: '#10B981', unit: '%' },
        { key: 'Temperature', label: 'Temp (°C)', icon: 'mdi-thermometer', color: '#F59E0B', unit: '°C' },
        { key: 'Voltage', label: 'Voltage (V)', icon: 'mdi-flash', color: '#8B5CF6', unit: 'V' },
        { key: 'Current.Import', label: 'Courant (A)', icon: 'mdi-current-ac', color: '#EC4899', unit: 'A' },
        { key: 'Frequency', label: 'Fréquence (Hz)', icon: 'mdi-sine-wave', color: '#06B6D4', unit: 'Hz' },
        { key: 'Power.Factor', label: 'Cos φ', icon: 'mdi-angle-acute', color: '#84CC16', unit: '' },
      ]

      for (const check of measurandChecks) {
        const hasIt = this.metrics.some(m =>
          m.measures?.some(x => x.measurand === check.key)
        )
        if (hasIt) {
          available.push(check)
        }
      }

      return available
    },

    // Additional series for selected metrics
    additionalSeries() {
      const series = []

      for (const metricKey of this.selectedMetrics) {
        const config = this.availableMetrics.find(m => m.key === metricKey)
        if (!config) continue

        const data = this.processedData
          .filter(d => d[metricKey]?.value !== undefined)
          .map(d => [d.timestamp, d[metricKey].value])

        if (data.length > 0) {
          series.push({
            name: config.label,
            type: 'line',
            data,
            color: config.color,
            yAxis: 2, // Third Y-axis for additional metrics
            tooltip: {
              valueSuffix: ` ${config.unit}`,
            },
            marker: {
              radius: 3,
            },
            lineWidth: 2,
          })
        }
      }

      return series
    },

    // Highcharts options
    chartOptions() {
      const self = this

      return {
        chart: {
          height: this.height,
          zoomType: 'x',
          backgroundColor: 'transparent',
          style: {
            fontFamily: 'inherit',
          },
        },

        time: {
          useUTC: false, // Use local timezone instead of UTC
        },

        title: {
          text: null,
        },

        credits: {
          enabled: false,
        },

        legend: {
          enabled: true,
          align: 'left',
          verticalAlign: 'top',
          floating: false,
          itemStyle: {
            fontSize: '11px',
            fontWeight: '500',
          },
        },

        xAxis: {
          type: 'datetime',
          plotLines: this.eventPlotLines,
          labels: {
            format: '{value:%H:%M}',
            style: {
              fontSize: '10px',
            },
          },
          gridLineWidth: 1,
          gridLineColor: '#F1F5F9',
        },

        yAxis: [
          {
            // Primary Y-axis - Power (kW)
            title: {
              text: 'Puissance (kW)',
              style: { color: '#E34A33', fontSize: '11px' },
            },
            labels: {
              format: '{value}',
              style: { color: '#E34A33', fontSize: '10px' },
            },
            min: 0,
            gridLineColor: '#F1F5F9',
          },
          {
            // Secondary Y-axis - Energy (kWh)
            title: {
              text: 'Énergie (kWh)',
              style: { color: '#3B82F6', fontSize: '11px' },
            },
            labels: {
              format: '{value}',
              style: { color: '#3B82F6', fontSize: '10px' },
            },
            opposite: true,
            gridLineWidth: 0,
          },
          {
            // Third Y-axis for additional metrics
            title: {
              text: '',
            },
            labels: {
              enabled: false,
            },
            opposite: true,
            gridLineWidth: 0,
            visible: this.selectedMetrics.length > 0,
          },
        ],

        tooltip: {
          shared: true,
          crosshairs: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          shadow: true,
          headerFormat: '<span style="font-size: 11px; font-weight: bold;">{point.key:%H:%M:%S}</span><br/>',
          style: {
            fontSize: '12px',
          },
        },

        plotOptions: {
          series: {
            animation: {
              duration: 500,
            },
          },
          areaspline: {
            fillOpacity: 0.3,
            lineWidth: 2,
            marker: {
              enabled: false,
              radius: 3,
              symbol: 'circle',
            },
          },
        },

        series: [
          {
            name: `Puissance (kW)${!this.hasPowerData ? ' - calculée' : ''}`,
            type: 'areaspline',
            data: this.powerSeriesData,
            color: '#E34A33',
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, 'rgba(227, 74, 51, 0.4)'],
                [1, 'rgba(227, 74, 51, 0.05)'],
              ],
            },
            yAxis: 0,
            tooltip: {
              valueSuffix: ' kW',
            },
          },
          {
            name: 'Énergie (kWh)',
            type: 'spline',
            data: this.energySeriesData,
            color: '#3B82F6',
            yAxis: 1,
            tooltip: {
              valueSuffix: ' kWh',
            },
            marker: {
              enabled: true,
              radius: 3,
              symbol: 'circle',
            },
            lineWidth: 2,
          },
          ...this.additionalSeries,
        ],
      }
    },
  },

  methods: {
    isMetricSelected(key) {
      return this.selectedMetrics.includes(key)
    },

    toggleMetric(key) {
      const idx = this.selectedMetrics.indexOf(key)
      if (idx >= 0) {
        this.selectedMetrics.splice(idx, 1)
      } else {
        this.selectedMetrics.push(key)
      }
    },

    normalizeToKwh(value, unit) {
      if (!unit) return value
      const u = unit.toLowerCase()
      if (u === 'kwh') return value
      if (u === 'wh') return value / 1000
      if (u === 'mwh') return value * 1000
      return value
    },

    normalizeToKw(value, unit) {
      if (!unit) return value
      const u = unit.toLowerCase()
      if (u === 'kw') return value
      if (u === 'w') return value / 1000
      if (u === 'mw') return value * 1000
      return value
    },
  },
}
</script>

<style lang="scss" scoped>
$text-muted: #64748B;
$border-color: #E2E8F0;
$primary: #E34A33;

.session-metrics-chart {
  width: 100%;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid $border-color;
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.controls-label {
  font-size: 12px;
  font-weight: 600;
  color: $text-muted;
  margin-right: 4px;
}

.max-power-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba($primary, 0.08);
  border: 1px solid rgba($primary, 0.2);
  border-radius: 8px;
  white-space: nowrap;
}

.max-power-label {
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  text-transform: uppercase;
}

.max-power-value {
  font-size: 14px;
  font-weight: 700;
  color: $primary;
}

.no-data-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background: #F8FAFC;
  border: 2px dashed $border-color;
  border-radius: 12px;

  p {
    margin-top: 12px;
    font-size: 14px;
    color: $text-muted;
  }
}
</style>
